/**
 * The WordprocessingML names the redline engine branches on, in one place.
 *
 * Every module in this package, the add-in's anchor package and the web
 * routes read namespaces, tag sets and DOM constants from here, so a tag set
 * is never spelled twice and a namespace URI never appears inline. The sets
 * are grouped by the question the engine asks of a node: is it text, is it a
 * glyph, may it be edited, does it wrap a revision, does it own its content.
 */

/** The main WordprocessingML namespace, prefix `w:` in every document Word writes. */
export const WORDPROCESSINGML_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
/** The Word 2010 extension namespace, prefix `w14:`, which carries `paraId` and `textId`. */
export const WORDML_2010_NS = "http://schemas.microsoft.com/office/word/2010/wordml";
/** The XML namespace itself, needed to write `xml:space="preserve"`. */
export const XML_NS = "http://www.w3.org/XML/1998/namespace";
/** The flat OPC package namespace, prefix `pkg:`, that wraps every OOXML blob Office.js hands out or accepts. */
export const PACKAGE_NS = "http://schemas.microsoft.com/office/2006/xmlPackage";
/**
 * The prefix of the retiring paragraph bookmark shape, `_dioptra_p_` plus hex:
 * the `paragraph_id` prod's minters and the add-in's flag-off pass write. The
 * three-word naming strips names with this prefix before it names a document.
 */
export const LEGACY_PARAGRAPH_BOOKMARK_PREFIX = "_dioptra_p_";
/**
 * The shape of a three-word paragraph bookmark, the `paragraph_id` the engine
 * addresses a paragraph by: an underscore, then three lowercase words of 3 to 7
 * letters joined by underscores, each word from `PARAGRAPH_ID_WORDS`.
 *
 * WHY this shape
 *   - the leading underscore keeps the bookmark hidden from Word's Bookmarks
 *     dialog, and Word's UI refuses a name that does not start with a letter, so
 *     no user-made bookmark can ever match
 *   - Word's own hidden bookmarks (`_GoBack`, `_Toc123456789`, `_Hlk…`) carry
 *     capitals or digits and fail the shape
 *   - legacy `_dioptra_p_<hex>` fails it: `p` is one letter and hex carries digits
 *
 * EXAMPLE
 *   `_result_prev_signed`   -> matches
 *   `_dioptra_p_3f59dbfc`   -> no match
 *   `_GoBack`               -> no match
 */
export const PARAGRAPH_BOOKMARK_SHAPE = /^_[a-z]{3,7}_[a-z]{3,7}_[a-z]{3,7}$/;
/**
 * Finds each paragraph id sitting inside a run of prose, in both shapes, so
 * display code can take them out of text a user reads. The two shapes above
 * test one whole bookmark name; this one scans, and must stay in step with them.
 *
 * WHY display code needs it
 *   A model writes paragraph ids freely in its own reasoning, which the chat
 *   streams to the user. The ids name nothing the user can act on, and the
 *   legacy shape spells out the product name the assistant otherwise conceals.
 *
 * EXAMPLE
 *   "Move _dioptra_p_3f59dbfc above _result_prev_signed"
 *   -> matches `_dioptra_p_3f59dbfc` and `_result_prev_signed`
 *
 * EDGE CASE
 *   The surrounding guards keep a longer snake_case symbol whose tail reads like
 *   a three-word id from matching: in `run_read_doc_now`, `_read_doc_now` is
 *   preceded by a word character, so the symbol is left whole.
 *
 * WHY the legacy tail takes capitals the minters never write
 *   `hexdigest()` is lowercase, so a name read off a document always is too, but
 *   this scans what a model retyped from one. Missing a tail it capitalised
 *   would show the user the product name, and widening the class costs nothing.
 *   The three-word shape stays lowercase: capitals are what tell Word's own
 *   `_GoBack` and `_Toc123` apart from an id.
 */
export const PARAGRAPH_BOOKMARK_NAME_IN_PROSE = /(?<![\w-])(?:_dioptra_p_[0-9a-fA-F]+|_[a-z]{3,7}_[a-z]{3,7}_[a-z]{3,7})(?![\w-])/g;
/** Word refuses a bookmark name longer than this; a legacy name is the prefix plus 29 hex characters, a three-word name is at most 24. */
export const WORD_BOOKMARK_NAME_MAX_LENGTH = 40;
/** The Word 2016 comment-id namespace, prefix `w16cid:`, which carries each comment's durable id. */
export const WORDML_2016_CID_NS = "http://schemas.microsoft.com/office/word/2016/wordml/cid";
/** The Word 2012 extension namespace, prefix `w15:`, which carries each comment's resolved state and the comment it replies to. */
export const WORDML_2012_NS = "http://schemas.microsoft.com/office/word/2012/wordml";
/** The package part names of the three comment parts, as `pkg:name` spells them. */
export const COMMENTS_PART_NAME = "/word/comments.xml";
export const COMMENTS_IDS_PART_NAME = "/word/commentsIds.xml";
export const COMMENTS_EXTENDED_PART_NAME = "/word/commentsExtended.xml";
/** The package part names of the numbering definitions and the styles, as `pkg:name` spells them; list labels are computed from both. */
export const NUMBERING_PART_NAME = "/word/numbering.xml";

/** The main document's relationships part in a flat OPC package: where a `w:hyperlink`'s `r:id` finds its address. */
export const DOCUMENT_RELATIONSHIPS_PART_NAME = "/word/_rels/document.xml.rels";

/** The main document part in a flat OPC package, as `pkg:name` spells it: the part holding the body every reader walks. */
export const MAIN_DOCUMENT_PART_NAME = "/word/document.xml";

/** The namespace of `r:id` on a `w:hyperlink`, and of the relationship types. */
export const OFFICE_RELATIONSHIPS_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

/** The namespace of the `Relationship` elements of a `.rels` part. */
export const PACKAGE_RELATIONSHIPS_NS = "http://schemas.openxmlformats.org/package/2006/relationships";

/** The type of a relationship whose `Target` is a hyperlink's address. */
export const HYPERLINK_RELATIONSHIP_TYPE = `${OFFICE_RELATIONSHIPS_NS}/hyperlink`;
export const STYLES_PART_NAME = "/word/styles.xml";
/** The run child that anchors a comment to the text around it; carries the comment's `w:id`. */
export const COMMENT_REFERENCE_TAG = "commentReference";
/** The two markers bracketing the text a comment highlights; both carry that comment's `w:id`. */
export const COMMENT_RANGE_START_TAG = "commentRangeStart";
export const COMMENT_RANGE_END_TAG = "commentRangeEnd";

/**
 * Every namespace a bare paragraph fragment from Word may use. A fragment
 * carries no declarations of its own, so `OoxmlRange` wraps it in an element
 * declaring all of these before parsing; a prefix the parser cannot resolve
 * would otherwise make the whole fragment unparseable.
 */
/**
 * SCOPE
 *   Complete for the paragraph-sized OOXML fragments this package reparses today.
 *
 * WHY
 *   Callers prepend these declarations, in this order, before `OoxmlRange` parsing.
 *   -> A fragment using `w:r`, `m:oMath`, and `wp:inline` resolves all three
 *      prefixes without carrying its own declarations.
 */
export const FRAGMENT_NAMESPACE_DECLARATIONS = [
    `xmlns:w="${WORDPROCESSINGML_NS}"`,
    `xmlns:w14="${WORDML_2010_NS}"`,
    'xmlns:w15="http://schemas.microsoft.com/office/word/2012/wordml"',
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"',
    'xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math"',
    'xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"',
    'xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"',
    'xmlns:v="urn:schemas-microsoft-com:vml"',
] as const;

/** The two elements that hold paragraph characters: live text and struck text. */
/**
 * SCOPE
 *   Full set of element-text nodes that contribute visible paragraph characters
 *   here.
 *
 * CASES
 *   - `w:t`        -> live text
 *   - `w:delText`  -> tracked-deletion text
 *   - `w:instrText` -> excluded: field code, not paragraph text to anchor against
 */
export const TEXT_TAGS = new Set(["t", "delText"]);

/**
 * Structural elements that read as one character of text, keyed by tag name.
 * `w:cr` is rare; Word emits it for a hard carriage return. The table keeps its
 * literal keys so a lookup is only possible through the `GlyphTag` guard below,
 * which is what stops an arbitrary tag name from indexing it.
 */
export const GLYPH_CHARS = { tab: "\t", br: "\n", cr: "\n" } as const satisfies Record<string, string>;
export type GlyphTag = keyof typeof GLYPH_CHARS;
/**
 * WHY
 *   Uses own-property membership.
 *   -> Only the literal glyph keys on `GLYPH_CHARS` pass.
 *   -> An input like `toString` is rejected even though plain objects inherit
 *      that property name.
 */
export const isGlyphTag = (tagName: string): tagName is GlyphTag => Object.hasOwn(GLYPH_CHARS, tagName);

/**
 * Characters that must be written as elements rather than inside `w:t`, keyed
 * by the character. The splicer splits requested text on these and narrows each
 * part with `isGlyphChar` before it looks the element name up.
 */
/**
 * CASES
 *   - read `w:br`  -> `"\n"`
 *   - read `w:cr`  -> `"\n"`
 *   - write `"\n"` -> emit `w:br`
 *   - never write  -> `w:cr`
 *
 * WHY
 *   Canonicalizes the shared paragraph character to the spelling Word normally
 *   writes for editable text.
 */
export const GLYPH_ELEMENTS = { "\t": "w:tab", "\n": "w:br" } as const satisfies Record<string, string>;
export type GlyphChar = keyof typeof GLYPH_ELEMENTS;
/**
 * WHY
 *   Uses own-property membership.
 *   -> Only the characters this writer can re-emit pass.
 *
 * EDGE CASE
 *   `"constructor" in GLYPH_ELEMENTS` would be true through the prototype chain,
 *   but `Object.hasOwn` rejects it.
 */
export const isGlyphChar = (character: string): character is GlyphChar => Object.hasOwn(GLYPH_ELEMENTS, character);

/**
 * Containers whose displayed text Word redraws from its own markup, so a
 * tracked change written inside one is thrown away on the next field update.
 * This is the nested field spelling; the flat `w:fldChar begin/separate/end`
 * spelling has no container and is tracked by `ParagraphModel` as it walks the
 * runs in order. Field instruction text (`w:instrText`) needs no entry: it is
 * not one of `TEXT_TAGS`, so it never becomes paragraph text at all.
 *
 * An equation (`m:oMath`) is not here. Word never recomputes one, and a tracked
 * change inside it is written in the math spelling the splicer emits: the
 * `w:ins` or `w:del` inside the `m:r`, around an `m:t` that stays `m:t`.
 *
 * CONSEQUENCE
 *   The text of a construct listed here is read for anchoring but never cut.
 *   An edit that reaches it unlinks the field first (`WordOwnedTextUnlinker`),
 *   which leaves the same characters as ordinary runs.
 *
 * EDGE CASE
 *   A `w:fldSimple` whose field Word does not redraw (`fieldResultIsRedrawnByWord`,
 *   a HYPERLINK) is not opaque: its result is the author's text, edited in place.
 */
export const OPAQUE_TAGS = new Set(["fldSimple"]);

/** The run children that carry a complex field's instruction, in the live and the struck spelling. */
export const FIELD_INSTRUCTION_TAGS = new Set(["instrText", "delInstrText"]);

/**
 * The field types whose result is the author's own text, which Word keeps as it
 * stands when fields update: a HYPERLINK's result is the link's display text.
 * Word redraws the result of every other field (REF, PAGEREF, SEQ, TOC, DATE,
 * PAGE, DOCVARIABLE, …) from its instruction.
 */
const FIELD_TYPES_WITH_AUTHOR_WRITTEN_RESULT = new Set(["HYPERLINK"]);

/**
 * Whether Word redraws the result of the field with this instruction, read from
 * the instruction's first word, the field type. One answer for every reader of a
 * field: `ParagraphModel` marks the result's text, `WordOwnedTextUnlinker` decides
 * what an edit unlinks, and `ParagraphSplicer` decides what a restored deletion
 * brings back whole.
 *
 * CASES
 *   - HYPERLINK              -> false: the display text is the author's, edited in place and tracked,
 *                               the field kept, so the link still works
 *   - any other field type   -> true: a tracked change in its result is thrown away when fields update
 *   - no instruction read    -> true: a field of unknown type is treated as Word's
 *
 * EXAMPLE
 *   ' HYPERLINK "https://x.example/fee" \h '   first word "HYPERLINK" -> false
 *   ' hyperlink \l "_Toc123" '                 first word "HYPERLINK" -> false (field codes ignore case)
 *   ' REF _Ref123 \h '                         first word "REF"       -> true
 *   ''                                         first word ""          -> true
 */
export const fieldResultIsRedrawnByWord = (fieldInstruction: string): boolean => !FIELD_TYPES_WITH_AUTHOR_WRITTEN_RESULT.has(fieldTypeOf(fieldInstruction));

/**
 * The field type an instruction opens with, upper-cased: field codes ignore case.
 *
 * EMPTY
 *   "" for an instruction with no argument.
 */
const fieldTypeOf = (fieldInstruction: string): string => {
    const [fieldTypeArgument] = fieldInstructionArgumentsOf(fieldInstruction);
    /** EMPTY instruction: no field type to read. */
    if (fieldTypeArgument === undefined) return "";
    return fieldTypeArgument.text.toUpperCase();
};

/** One argument of a field instruction: its text, unquoted and unescaped, and whether it was quoted. */
export interface FieldInstructionArgument {
    text: string;
    /** True for an argument written in double quotes: never a switch, whatever it starts with. */
    quoted: boolean;
}

/** The character that opens and closes a quoted field argument. */
const FIELD_ARGUMENT_QUOTE = '"';

/** The character that escapes a quote or a backslash inside a quoted field argument, and that opens a switch. */
const FIELD_ARGUMENT_ESCAPE = "\\";

/** Whitespace between the arguments of a field instruction. */
const FIELD_ARGUMENT_SEPARATOR = /\s/u;

/**
 * The arguments of a field instruction in order, the one reading of an instruction's
 * words: the field type first, then each switch and value. A quoted argument keeps
 * its spaces, and a backslash inside it escapes the next character, as Word writes a
 * quote or a backslash inside one.
 *
 * EXAMPLE
 *   ' HYPERLINK "https://x.example/a b" \l "Fees" \o "Fee \"table\"" '
 *   -> HYPERLINK, https://x.example/a b (quoted), \l, Fees (quoted), \o, Fee "table" (quoted)
 *   ' REF _Ref123 \h '
 *   -> REF, _Ref123, \h
 */
export const fieldInstructionArgumentsOf = (fieldInstruction: string): FieldInstructionArgument[] => {
    const fieldArguments: FieldInstructionArgument[] = [];
    let characterIndex = 0;
    while (characterIndex < fieldInstruction.length) {
        /** WHEN whitespace stands between arguments CONSEQUENCE it is passed over. */
        if (FIELD_ARGUMENT_SEPARATOR.test(fieldInstruction[characterIndex])) {
            characterIndex += 1;
            continue;
        }
        const argumentRead = fieldArgumentAt(fieldInstruction, characterIndex);
        fieldArguments.push(argumentRead.fieldArgument);
        characterIndex = argumentRead.indexAfterArgument;
    }
    return fieldArguments;
};

/** One argument read off an instruction, and the index of the first character after it. */
interface FieldArgumentRead {
    fieldArgument: FieldInstructionArgument;
    indexAfterArgument: number;
}

/**
 * The argument that starts at `startIndex`, a character that is not whitespace.
 *
 * CASES
 *   - a quote opens it -> read up to the quote that closes it (`quotedFieldArgumentAt`)
 *   - anything else    -> read up to the next whitespace (`bareFieldArgumentAt`)
 */
const fieldArgumentAt = (fieldInstruction: string, startIndex: number): FieldArgumentRead => {
    /**
     * CASES
     *   - the argument opens with a quote -> read up to its closing quote: `"https://x.example/fee"`
     *   - otherwise                       -> read up to the next separator: `https://x.example/fee`
     */
    if (fieldInstruction[startIndex] === FIELD_ARGUMENT_QUOTE) return quotedFieldArgumentAt(fieldInstruction, startIndex);
    return bareFieldArgumentAt(fieldInstruction, startIndex);
};

/**
 * The quoted argument whose opening quote is at `openingQuoteIndex`.
 *
 * EDGE CASE
 *   An argument whose closing quote is missing runs to the instruction's end.
 *
 * EXAMPLE
 *   ' "a \"b\" c" ' from 1 -> text 'a "b" c'; the closing quote is at 11, so the argument ends at `11 + 1` = 12
 */
const quotedFieldArgumentAt = (fieldInstruction: string, openingQuoteIndex: number): FieldArgumentRead => {
    let argumentText = "";
    /** The first character inside the quotes: one past the opening quote. */
    let characterIndex = openingQuoteIndex + 1;
    while (characterIndex < fieldInstruction.length && fieldInstruction[characterIndex] !== FIELD_ARGUMENT_QUOTE) {
        /** A backslash escapes only when a character follows it: `characterIndex + 1` is that character, inside the instruction. */
        const escapesTheNextCharacter = fieldInstruction[characterIndex] === FIELD_ARGUMENT_ESCAPE && characterIndex + 1 < fieldInstruction.length;
        /**
         * WHEN a backslash escapes the character after it
         *   CONSEQUENCE
         *     that character is taken as written, and both are passed: `characterIndex + 1` is the
         *     escaped character, `+ 2` the one after it. EXAMPLE '\"' at 3 -> '"' taken, next at 5
         */
        if (escapesTheNextCharacter) {
            argumentText += fieldInstruction[characterIndex + 1];
            characterIndex += 2;
            continue;
        }
        argumentText += fieldInstruction[characterIndex];
        characterIndex += 1;
    }
    /** Past the closing quote: one past its index. */
    return { fieldArgument: { text: argumentText, quoted: true }, indexAfterArgument: characterIndex + 1 };
};

/**
 * The unquoted argument that starts at `startIndex`, up to the next whitespace.
 *
 * EXAMPLE
 *   ' REF _Ref123 \h ' from 5 -> "_Ref123", ending at 12
 */
const bareFieldArgumentAt = (fieldInstruction: string, startIndex: number): FieldArgumentRead => {
    let characterIndex = startIndex;
    /** Steps to the first separator, or the instruction's end: ' REF _Ref123 \h ' from 5 stops at 12, the space after "_Ref123". */
    while (characterIndex < fieldInstruction.length && !FIELD_ARGUMENT_SEPARATOR.test(fieldInstruction[characterIndex])) characterIndex += 1;
    /** The argument is `startIndex` up to that separator, which it does not include: 5..12 -> "_Ref123". */
    return { fieldArgument: { text: fieldInstruction.slice(startIndex, characterIndex), quoted: false }, indexAfterArgument: characterIndex };
};

/** The HYPERLINK switches followed by a value: the place inside the target (`\l`), the tip (`\o`), the frame (`\t`). */
const HYPERLINK_SWITCHES_TAKING_A_VALUE = new Set(["\\l", "\\o", "\\t"]);

/** The HYPERLINK switch whose value is the place inside the target, a bookmark name. */
const HYPERLINK_PLACE_SWITCH = "\\l";

/**
 * The address a HYPERLINK field instruction points to, as `linkAddressOf` spells it.
 *
 * CASES
 *   - a field of another type                -> null
 *   - a target, and a place inside it (`\l`) -> the target, "#", the place
 *   - a target alone                          -> the target
 *   - a place alone                           -> "#" and the place: a bookmark of this document
 *   - neither                                 -> null
 *
 * EXAMPLE
 *   ' HYPERLINK "https://x.example/fee" \o "Fees" '  -> "https://x.example/fee"
 *   ' HYPERLINK \l "Schedule_1" '                    -> "#Schedule_1"
 *   ' HYPERLINK "https://x.example/" \l "fees" '     -> "https://x.example/#fees"
 *   ' REF _Ref123 \h '                               -> null
 */
export const hyperlinkAddressOfFieldInstruction = (fieldInstruction: string): string | null => {
    const [fieldTypeArgument, ...switchesAndValues] = fieldInstructionArgumentsOf(fieldInstruction);
    /** NULL: no field type, or a field that is not a link. */
    if (fieldTypeArgument === undefined || fieldTypeArgument.text.toUpperCase() !== "HYPERLINK") return null;
    let target: string | null = null;
    let placeInTarget: string | null = null;
    for (let argumentIndex = 0; argumentIndex < switchesAndValues.length; argumentIndex += 1) {
        const fieldArgument = switchesAndValues[argumentIndex];
        const switchName = fieldArgument.text.toLowerCase();
        const isSwitch = !fieldArgument.quoted && fieldArgument.text.startsWith(FIELD_ARGUMENT_ESCAPE);
        /**
         * WHEN a switch takes a value
         *   CONSEQUENCE
         *     the value is the next argument, read for `\l` and passed over for the others:
         *     `argumentIndex + 1` is the value, and the loop's own `+ 1` moves past it.
         *   EXAMPLE \l at 1 -> the place is argument 2; the loop goes on at 3
         */
        if (isSwitch && HYPERLINK_SWITCHES_TAKING_A_VALUE.has(switchName)) {
            const switchValue = switchesAndValues[argumentIndex + 1];
            /** NULL value: a switch at the instruction's end, with nothing after it, names no place. */
            if (switchName === HYPERLINK_PLACE_SWITCH && switchValue !== undefined) placeInTarget = switchValue.text;
            argumentIndex += 1;
            continue;
        }
        /** WHEN a switch takes no value (`\m`, `\n`, `\h`) CONSEQUENCE it says nothing of the address. */
        if (isSwitch) continue;
        /** WHEN the target was read already CONSEQUENCE a second value says nothing of the address. */
        if (target !== null) continue;
        target = fieldArgument.text;
    }
    return linkAddressOf(target, placeInTarget);
};

/**
 * A link's address as every reader shows it: the target, then "#" and the place inside
 * it where the link names one, the spelling a browser and Word both read.
 *
 * CASES
 *   - target and place -> "<target>#<place>"
 *   - target alone     -> "<target>"
 *   - place alone      -> "#<place>": a bookmark of this document
 *   - neither          -> null
 */
export const linkAddressOf = (target: string | null, placeInTarget: string | null): string | null => {
    /** NULL: the link names neither a target nor a place in one. */
    if (target === null && placeInTarget === null) return null;
    /** WHEN the link names a target alone CONSEQUENCE the target is its address: "https://x.example/fee". */
    if (placeInTarget === null) return target;
    /** WHEN the link names a place alone CONSEQUENCE a bookmark of this document: "#_pay". */
    if (target === null) return `#${placeInTarget}`;
    return `${target}#${placeInTarget}`;
};

/**
 * A `w:fldSimple`'s instruction, read in both spellings Word writes the attribute
 * in; "" for one that carries none.
 *
 * FALLBACK
 *   - namespace-aware `instr` present -> use it
 *   - otherwise                       -> the prefixed `w:instr`, which a paragraph parsed out of the document carries
 */
export const fieldSimpleInstructionOf = (fieldSimple: Element): string =>
    fieldSimple.getAttributeNS(WORDPROCESSINGML_NS, "instr") ?? fieldSimple.getAttribute("w:instr") ?? "";
/**
 * Run children that hold a picture or an embedded object rather than text. The
 * agent view shows each as `<image/>` so the model knows something sits there
 * that it cannot read; the accepted text ignores them.
 */
export const INLINE_OBJECT_TAGS = new Set(["drawing", "object", "pict"]);
/** `w:val` spellings that turn a toggle property such as `w:strike` off. */
export const TOGGLE_OFF_VALUES = new Set(["false", "0", "off", "none"]);
/** The run child that marks a complex field's begin, separate and end: `w:fldChar`. */
export const FIELD_CHAR_TAG = "fldChar";
/** The three states of a complex field's `w:fldChar` marker, in document order. */
export const FIELD_CHAR_TYPES = { begin: "begin", separate: "separate", end: "end" } as const satisfies Record<string, string>;
export type FieldCharType = keyof typeof FIELD_CHAR_TYPES;
/**
 * WHEN
 *   The raw `w:fldCharType` attribute text is checked against Word's marker
 *   tokens.
 *
 * CONSEQUENCE
 *   CASES
 *     - `begin`     -> narrows to `FieldCharType`
 *     - `separate`  -> narrows to `FieldCharType`
 *     - `end`       -> narrows to `FieldCharType`
 *     - any other string -> does not narrow
 */
export const isFieldCharType = (fieldCharTypeAttributeValue: string): fieldCharTypeAttributeValue is FieldCharType => Object.hasOwn(FIELD_CHAR_TYPES, fieldCharTypeAttributeValue);

/** Property blocks whose text is never paragraph text. */
export const PROPERTY_TAGS = new Set(["pPr", "rPr", "sdtPr", "sdtEndPr"]);
/** Containers a new `w:ins` may never nest inside. */
/**
 * CASES
 *   - `w:ins`     -> insertion revision
 *   - `w:moveTo`  -> destination half of a move
 *
 * CONSEQUENCE
 *   Both count as inserted-content wrappers for nesting rules.
 *   -> Creating a new insertion inside either would produce a forbidden
 *      insertion-within-insertion shape.
 */
export const INSERTION_CONTAINERS = new Set(["ins", "moveTo"]);
/** Containers whose content is struck text. */
/**
 * CASES
 *   - `w:del`       -> deletion revision
 *   - `w:moveFrom`  -> source half of a move
 *
 * CONSEQUENCE
 *   Text under either is handled as removed-from-document text.
 */
export const DELETION_CONTAINERS = new Set(["del", "moveFrom"]);
/** Every revision wrapper whose author matters for the "someone else's change" tests. */
export const REVISION_CONTAINERS = new Set([...INSERTION_CONTAINERS, ...DELETION_CONTAINERS]);
/** Formatting-change records; they carry an author and date like a revision but wrap no text. */
export const PROPERTY_CHANGE_TAGS = new Set(["rPrChange", "pPrChange"]);
/** Everything that carries `w:author` and `w:date`: what a restamp of a whole inserted paragraph must visit. */
export const REVISION_TAGS = new Set([...REVISION_CONTAINERS, ...PROPERTY_CHANGE_TAGS]);
/** Containers that own their text: replacement text stays inside the same one as the text it replaces. */
/**
 * The parts of an equation that hold their own runs: the base and the raised or
 * lowered arguments of a script, a fraction's numerator and denominator, a
 * radical's degree, a function's name, a limit. Each keeps the text struck
 * inside it, so a replacement for an exponent is written as an exponent and
 * never lands beside the equation as ordinary text.
 */
export const MATH_ARGUMENT_TAGS = new Set(["e", "sup", "sub", "num", "den", "deg", "fName", "lim"]);

/**
 * SCOPE
 *   Wrappers whose content must keep replacement text inside the same container.
 *
 * EXAMPLE
 *   `w:hyperlink`   -> replacement stays under that hyperlink
 *   `w:sdtContent`  -> replacement stays inside the content control
 *   `m:sup`         -> replacement for a struck exponent stays an exponent
 *
 * EDGE CASE
 *   A hyperlink none of whose content words an edit keeps is struck whole before
 *   the splice (`LinkFieldStriker`), and the replacement is written after it: new
 *   words never point at the old address.
 *
 * CONSEQUENCE
 *   Replacement text does not escape into the surrounding paragraph.
 */
export const STRUCTURAL_HOMES = new Set([
    "sdtContent",
    "hyperlink",
    "smartTag",
    "fldSimple",
    "customXml",
    ...MATH_ARGUMENT_TAGS,
    "oMath",
]);

/**
 * `Node.DOCUMENT_POSITION_FOLLOWING` spelled as a number. The web route runs
 * this code under xmldom, which implements `compareDocumentPosition` but
 * installs no `Node` global, so the browser constant is unavailable there.
 */
/**
 * TYPE
 *   Bit mask value for `Node.DOCUMENT_POSITION_FOLLOWING`, not a standalone enum.
 *
 * EXAMPLE
 *   compare result = 20
 *   mask value      = 4
 *   `20 & 4`        -> non-zero
 *   meaning         -> the other node follows this one in document order
 */
export const DOCUMENT_POSITION_FOLLOWING = 4;

/**
 * RETURNS
 *   Element name without its prefix.
 *
 * CASES
 *   - `w:t`   -> `t`
 *   - `t`     -> `t`
 *   - `a:b:c` -> `c`
 *
 * SCOPE
 *   Only for OOXML-style prefixed or unprefixed element names.
 */
export const elementNameWithoutPrefix = (node: Element): string => node.nodeName.replace(/^.*:/, "");

/**
 * The `w:id` an element carries, or null when it carries none.
 *
 * CASES
 *   - the attribute is namespaced, as Word writes it -> read it by namespace
 *   - a producer wrote the bare `w:id` attribute      -> read it by literal name
 *   - neither is present                              -> null, so the caller decides
 *                                                        what an id-less element means
 *
 * NULL
 *   A bookmark marker with no id is unusable and reads as `""`; a comment with no id
 *   is one nothing can point at and is skipped. Both are the caller's call, so this
 *   reader reports the absence rather than inventing a value.
 */
export const wordIdOf = (wordprocessingElement: Element): string | null =>
    wordprocessingElement.getAttributeNS(WORDPROCESSINGML_NS, "id") ?? wordprocessingElement.getAttribute("w:id");
